import { useEffect, useState } from "react";
import { Box } from "../../components/Box";
import { useAuth } from "../../context/AuthContext";
import { useDirtyForm } from "../../DirtyFormContext";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H2, H3 } from "../../components/Heading";
import { useClientDetails } from "../../data/useClientDetails";
import styled from "styled-components";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { useUpdateClient } from "../../data/useUpdateClient";
import { showError } from "../../utils/error-handling";
import { queryClient } from "../../queryClient";
import { notify } from "../../utils/notify";
import { Toggle } from "../../components/Toggle";
import Select from "../../components/Select";

const Heading = styled(H3)`
  ${({ theme: { fontSizes } }) => `
    font-size: ${fontSizes[1]};
    word-break: break-word;
    margin-bottom: 0px;
  `}
`;

const StyleParagraph = styled(Paragraph)`
  ${({ theme: { fontSizes, colors, space } }) => `
    font-size: ${fontSizes[1]};
    word-break: break-word;
    color: ${colors.gray[60]};
    margin-top: ${space[0]};
    margin-bottom: ${space[0]};
  `}
`;

const SettingsAddressHistory = () => {
  const { setFormDirty } = useDirtyForm();
  const { userDetails } = useAuth();
  const { data: client, isLoading } = useClientDetails(userDetails?.tenant);
  const { mutate: updateClient, isLoading: isUpdating } = useUpdateClient();
  const [capturePrevious, setCapturePrevious] = useState(false);
  const minimumAddressHistoryYearsDefault = 3;
  const [minimumAddressHistoryYears, setMinimumAddressHistoryYears] = useState(null);

  useEffect(() => {
    if (client?.data?.capture_previous_address !== undefined) {
      setCapturePrevious(client.data.capture_previous_address);
    }
    client?.data?.previous_address_history_min !== null ? setMinimumAddressHistoryYears(client?.data?.previous_address_history_min) : setMinimumAddressHistoryYears(3);
  }, [client]);

  const updateCapturePrevious = (requiredAddressHistory: boolean, minimumAddressHistoryYears: number) => {
    if (isUpdating) return;

    setFormDirty(true);

    setCapturePrevious(requiredAddressHistory);

    updateClient(
      {
        id: userDetails?.tenant,
        capture_previous_address: requiredAddressHistory,
        previous_address_history_min: minimumAddressHistoryYears,
      },
      {
        onSuccess: (res) => {
          if (res?.status?.toString().startsWith("2")) {
            notify("Capture previous address preference has been saved.", {
              type: "success",
            });
            queryClient.invalidateQueries([
              "clientDetails",
              userDetails?.tenant,
            ]);
          }
          setFormDirty(false);
        },
        onError: (err) => {
         // setCapturePrevious(!value, minimumAddressHistoryYearsDefault);
          setFormDirty(false);
          showError(
            err,
            "A problem occurred while updating the capture previous address preference."
          );
        },
      }
    );
  };

  let customNumberOptions = [];
  for (let i = 0; i < 10; i++) {
    var number = {
      label: (i + 1) + ' years',
      value: i + 1,
    };
    customNumberOptions.push(number);
  }

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    const minHistoryYears = (value === true ? minimumAddressHistoryYears : minimumAddressHistoryYearsDefault);
    updateCapturePrevious(value, minHistoryYears);
  };

  const handleReset = () => {
    updateCapturePrevious(false, minimumAddressHistoryYearsDefault);
  };
  const handleSelectChange = (event) => {
    setMinimumAddressHistoryYears(event.value);
    updateCapturePrevious(capturePrevious, event.value);
  }

  return (
    <Box bg="white" borderRadius={0} p={3} mb={3}>
      <Flex justifyContent="space-between" alignItems="center">
        <H2 mb={0}>Address History</H2>
        <Button
          onClick={handleReset}
          variant="text"
          type="button"
          fontWeight="bold"
        >
          Reset all
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minHeight="35vh">
          <Loader />
        </Flex>
      ) : (
        <>
          <Flex justifyContent="space-between" alignItems="center" my={3}>
            <Box>
              <StyleParagraph>
                Configure how address history is collected when capturing an individual residential address. These settings apply to all application types
                that include address capture.
              </StyleParagraph>
            </Box>
          </Flex>
          <Flex my={3} justifyContent="space-between" alignItems="center">
            <Box>
              <Heading>Require Address History</Heading>
              <StyleParagraph>
                When enabled, individuals must provide previous addresses if they have lived at their current address for less than the minimum address history period specified below.
              </StyleParagraph>
            </Box>
            <Box>
              <Toggle
                isChecked={capturePrevious}
                onChange={handleToggleChange}
                isDisabled={isUpdating}
              />
            </Box>
          </Flex>
          {capturePrevious && 
          <Flex my={3} justifyContent="space-between" alignItems="center">
            <Box width={"80%"}>
              <Heading>Minimum Address History</Heading>
              <StyleParagraph>
                The minimum number of years of residential address history required. If an individual has lived at their current address for less than this period,
                they will be prompted to add previous addresses until the total history meets this requirement.
              </StyleParagraph>
            </Box>
            <Box width={"20%"}>
              <Select
                placeholder="Select value"
                name="previous_address_history_min"
                value = {customNumberOptions?.find(
                  ({ value }) => value === minimumAddressHistoryYears
                )}
                options={customNumberOptions}
                onChange={handleSelectChange}
              />
            </Box>
          </Flex>
}
        </>
      )}
    </Box>
  );
};

export default SettingsAddressHistory;
